/**
 * WalletService — EIP-1193 / EIP-6963 wallet integration.
 *
 * Zero third-party web3 dependencies. Uses:
 *   - EIP-6963 provider discovery to enumerate injected wallets
 *     (MetaMask, Rabby, Coinbase Wallet, Trust, Brave …)
 *   - Raw window.ethereum.request for chain switching and sending
 *     transactions, so the user's wallet is always the signer.
 *   - EIP-681 payment URI + QR code as a fallback for mobile wallets
 *     that aren't installed as browser extensions (WalletConnect-free).
 *
 * SECURITY: never asks for private keys, seed phrases, or passwords.
 * Every transaction requires explicit approval inside the user's wallet.
 */

// Binance Smart Chain Mainnet
export const BSC_CHAIN = Object.freeze({
  chainIdHex: "0x38",
  chainIdInt: 56,
  chainName: "BNB Smart Chain",
  nativeCurrency: { name: "BNB", symbol: "BNB", decimals: 18 },
  rpcUrls: [
    "https://bsc-dataseed.binance.org/",
    "https://bsc-dataseed1.defibit.io/",
    "https://bsc-dataseed1.ninicoin.io/",
  ],
  blockExplorerUrls: ["https://bscscan.com"],
});

export const DONATION_ADDRESS = "0x7258c7a36e2c7B1ddd8aE3B816EC0191af42e75a";

// -------------------- EIP-6963 discovery --------------------

let _discovered = new Map(); // uuid -> { info, provider }
let _listenersAttached = false;

const ensureDiscovery = () => {
  if (_listenersAttached || typeof window === "undefined") return;
  _listenersAttached = true;
  window.addEventListener("eip6963:announceProvider", (e) => {
    const detail = e?.detail;
    if (!detail?.info || !detail?.provider) return;
    _discovered.set(detail.info.uuid, detail);
  });
  window.dispatchEvent(new Event("eip6963:requestProvider"));
};

/** Return a snapshot of currently discovered injected wallets. */
export const listInjectedWallets = () => {
  ensureDiscovery();
  const list = Array.from(_discovered.values()).map(({ info, provider }) => ({
    uuid: info.uuid,
    name: info.name,
    icon: info.icon,
    rdns: info.rdns,
    provider,
  }));
  // Fallback: some old wallets only expose window.ethereum without EIP-6963
  if (list.length === 0 && typeof window !== "undefined" && window.ethereum) {
    const eth = window.ethereum;
    const name = eth.isMetaMask
      ? "MetaMask"
      : eth.isRabby
      ? "Rabby"
      : eth.isCoinbaseWallet
      ? "Coinbase Wallet"
      : eth.isTrust
      ? "Trust"
      : eth.isBraveWallet
      ? "Brave"
      : "Wallet inyectada";
    list.push({ uuid: "legacy", name, icon: null, rdns: "legacy", provider: eth });
  }
  return list;
};

// -------------------- Connect --------------------

/** Ask the wallet for an account. Returns { provider, account }. */
export const connectWallet = async (provider) => {
  if (!provider?.request) throw new Error("Wallet provider not available");
  const accounts = await provider.request({ method: "eth_requestAccounts" });
  if (!accounts?.[0]) throw new Error("No account returned by the wallet");
  return { provider, account: accounts[0] };
};

/** Get current chain id in hex (e.g. "0x38"). */
export const getChainId = async (provider) => {
  const cid = await provider.request({ method: "eth_chainId" });
  return typeof cid === "string" ? cid.toLowerCase() : cid;
};

/** Ensure the wallet is on Binance Smart Chain, prompting the user
 *  to switch or add the chain if needed. */
export const ensureBSC = async (provider) => {
  const current = await getChainId(provider);
  if (current === BSC_CHAIN.chainIdHex) return true;
  try {
    await provider.request({
      method: "wallet_switchEthereumChain",
      params: [{ chainId: BSC_CHAIN.chainIdHex }],
    });
    return true;
  } catch (err) {
    // 4902 = unknown chain — try to add it and switch
    if (err?.code === 4902 || /Unrecognized chain/i.test(err?.message || "")) {
      await provider.request({
        method: "wallet_addEthereumChain",
        params: [
          {
            chainId: BSC_CHAIN.chainIdHex,
            chainName: BSC_CHAIN.chainName,
            nativeCurrency: BSC_CHAIN.nativeCurrency,
            rpcUrls: BSC_CHAIN.rpcUrls,
            blockExplorerUrls: BSC_CHAIN.blockExplorerUrls,
          },
        ],
      });
      return true;
    }
    throw err;
  }
};

// -------------------- Value helpers --------------------

/** Convert a decimal BNB string like "0.25" to a wei hex string. */
export const bnbToWeiHex = (amount) => {
  const s = String(amount).trim();
  if (!/^\d+(\.\d+)?$/.test(s)) throw new Error("Cantidad no válida");
  const [whole, frac = ""] = s.split(".");
  const fracPadded = (frac + "0".repeat(18)).slice(0, 18);
  const combined = (whole + fracPadded).replace(/^0+(?=\d)/, "");
  const wei = BigInt(combined || "0");
  if (wei <= 0n) throw new Error("La cantidad debe ser mayor que 0");
  return "0x" + wei.toString(16);
};

// -------------------- Send transaction --------------------

/** Send a native BNB transfer of `amount` BNB from `from` to `DONATION_ADDRESS`.
 *  Returns the transaction hash returned by the wallet. */
export const sendDonation = async ({ provider, from, amount }) => {
  if (!provider?.request) throw new Error("Wallet no conectada");
  const value = bnbToWeiHex(amount);
  const tx = {
    from,
    to: DONATION_ADDRESS,
    value,
    // Let the wallet estimate gas; do NOT set gasPrice so users' wallet
    // chooses the recommended fee (BSC is EIP-1559 friendly).
  };
  const hash = await provider.request({ method: "eth_sendTransaction", params: [tx] });
  return hash;
};

/** Poll for a transaction receipt (via the wallet's provider). */
export const waitForReceipt = async (provider, hash, { intervalMs = 3000, timeoutMs = 180000 } = {}) => {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    try {
      const r = await provider.request({
        method: "eth_getTransactionReceipt",
        params: [hash],
      });
      if (r) return r;
    } catch {
      /* ignore transient RPC errors */
    }
    await new Promise((res) => setTimeout(res, intervalMs));
  }
  return null;
};

// -------------------- Payment URI + QR fallback --------------------

/** EIP-681 payment URI for wallet apps (Trust, TokenPocket, SafePal, etc.). */
export const buildPaymentURI = (amount) => {
  const value = bnbToWeiHex(amount).replace(/^0x/, "");
  const wei = BigInt("0x" + value).toString();
  return `ethereum:${DONATION_ADDRESS}@${BSC_CHAIN.chainIdInt}?value=${wei}`;
};

/** Generate a QR-code SVG (via the `qrcode` npm package). */
export const generateQRSvg = async (payload) => {
  const QRCode = (await import("qrcode")).default;
  return await QRCode.toString(payload, {
    type: "svg",
    color: { dark: "#8A6BFF", light: "#0B0B12" },
    margin: 1,
    errorCorrectionLevel: "M",
  });
};

// -------------------- Chain events --------------------

/** Subscribe to wallet chain / account change. Returns unsubscribe. */
export const subscribeWallet = (provider, { onChain, onAccounts, onDisconnect } = {}) => {
  if (!provider?.on) return () => {};
  const chainHandler = (id) => onChain?.(String(id).toLowerCase());
  const acctHandler = (accs) => onAccounts?.(accs);
  const discHandler = () => onDisconnect?.();
  provider.on("chainChanged", chainHandler);
  provider.on("accountsChanged", acctHandler);
  provider.on("disconnect", discHandler);
  return () => {
    provider.removeListener?.("chainChanged", chainHandler);
    provider.removeListener?.("accountsChanged", acctHandler);
    provider.removeListener?.("disconnect", discHandler);
  };
};

// -------------------- Public utility --------------------

/** Short-format a 0x address for UI. */
export const shortAddress = (a) => (a ? `${a.slice(0, 6)}…${a.slice(-4)}` : "—");

/** BscScan URL for a tx hash. */
export const bscScanTxUrl = (hash) => `${BSC_CHAIN.blockExplorerUrls[0]}/tx/${hash}`;
